import type { PolicyVersionDto, PostTypeSummaryDto, VehicleTypeSummaryDto } from '@pams/shared';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [FIX-1] THE ONE PLACE THAT DECIDES WHICH OF A POLICY VERSION'S COLUMNS ARE USABLE.
 *
 *  A published policy version's stored arrays are THE RECORD and they never change. P2
 *  froze them on purpose: an order raised last month must still render exactly as it was
 *  issued, and a version that mutates when a master row is archived is not frozen. So
 *  retiring a post type must not touch a single stored document.
 *
 *  What changes is what the record is USED for — and that is this function, which every
 *  consumer reads instead of the raw array:
 *
 *    • the grid's columns          • the seeded requirement lines
 *    • the totals and allocations  • the publish payload derived from them
 *    • the header's count          • whether the policy is offerable at all
 *
 *  ⚠️ ONE FUNCTION, NOT SIX FILTERS. The failure this prevents is not "somebody forgets to
 *  filter" — it is six places filtering slightly differently, so the header says four, the
 *  grid draws five, and the payload sends a sixth. `grep -rn "archivedAt" web/src` should
 *  find no other place deciding this about a policy column.
 *
 *  ⚠️ IT NEVER FILTERS AN ARRANGEMENT'S OWN LINES. An arrangement snapshots its requirement
 *  at publish, so a retirement must not alter an order that already exists — and a fill
 *  screen must never lose a column somebody is halfway through naming. `toGrid` resolves
 *  those lines against the FULL reference map for exactly that reason; this decides what a
 *  NEW arrangement is offered, which is a different question.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

export type PolicyColumnRow<T> = { id: string; summary: T };

export type PolicyColumns = {
  /** Usable post types, in the policy's stored order. What a new arrangement is offered. */
  postRows: PolicyColumnRow<PostTypeSummaryDto>[];
  vehicleRows: PolicyColumnRow<VehicleTypeSummaryDto>[];
  /**
   * ⚠️ EVERY column the version stores, usable or not — keyed by id.
   *
   * The GRID resolves labels from this rather than from `postRows`, so an arrangement that
   * already carries an archived column still renders it with its real name instead of "—".
   * That is exit criterion 3, and it is the one that would regress in silence.
   */
  allPostTypes: Map<string, PostTypeSummaryDto>;
  allVehicleTypes: Map<string, VehicleTypeSummaryDto>;
  /** How many stored columns are no longer usable. Disclosed, never hidden. */
  archivedPostCount: number;
  archivedVehicleCount: number;
  /** ⚠️ A policy with no usable post type cannot be used to raise anything. */
  isUsable: boolean;
};

const EMPTY: PolicyColumns = {
  postRows: [],
  vehicleRows: [],
  allPostTypes: new Map(),
  allVehicleTypes: new Map(),
  archivedPostCount: 0,
  archivedVehicleCount: 0,
  isUsable: false,
};

export const resolvePolicyColumns = (version: PolicyVersionDto | undefined): PolicyColumns => {
  if (!version) return EMPTY;

  const allPostTypes = new Map<string, PostTypeSummaryDto>();
  const postRows: PolicyColumnRow<PostTypeSummaryDto>[] = [];
  let archivedPostCount = 0;

  for (const row of version.postTypes) {
    if (!row.postType) continue;
    allPostTypes.set(row.postTypeId, row.postType);
    /*
     * ⚠️ [LIFECYCLE] `archivedAt == null` IS THE WHOLE PREDICATE, AND IT REPLACED
     * `isActive`. `== null` rather than `=== null` deliberately: it is also false for an
     * `undefined`, which is what a stale cache or an older wire shape produces, and an
     * unknown column must not be silently dropped — see `usableAmong` below.
     */
    if (row.postType.archivedAt == null) postRows.push({ id: row.postTypeId, summary: row.postType });
    else archivedPostCount += 1;
  }

  const allVehicleTypes = new Map<string, VehicleTypeSummaryDto>();
  const vehicleRows: PolicyColumnRow<VehicleTypeSummaryDto>[] = [];
  let archivedVehicleCount = 0;

  for (const row of version.vehicleTypes) {
    if (!row.vehicleType) continue;
    allVehicleTypes.set(row.vehicleTypeId, row.vehicleType);
    if (row.vehicleType.archivedAt == null) vehicleRows.push({ id: row.vehicleTypeId, summary: row.vehicleType });
    else archivedVehicleCount += 1;
  }

  return {
    postRows,
    vehicleRows,
    allPostTypes,
    allVehicleTypes,
    archivedPostCount,
    archivedVehicleCount,
    // A vehicle block alone cannot carry an arrangement: force is what an order asks for.
    isUsable: postRows.length > 0,
  };
};

/**
 * "4 posts · 1 archived" — what is usable, with what is not DISCLOSED rather than hidden.
 *
 * A bare "5" lies about what the operator can use, and a silent "4" hides that a column
 * they remember has gone. Both were on screen at once before this.
 *
 * ⚠️ [LIFECYCLE] THE WORD CHANGED FROM "retired" TO "archived", AND ONLY THE WORD. Three
 * screens said three different things for one fact — Deactivate, Retire, Deactivate — and
 * one vocabulary was the point of the pass. The arithmetic is untouched.
 */
export const columnCountLabel = (usable: number, archived: number, noun: string): string => {
  const base = `${String(usable)} ${noun}${usable === 1 ? '' : 's'}`;
  return archived > 0 ? `${base} · ${String(archived)} archived` : base;
};

/**
 * ⚠️ [FIX-1] THE SAME DECISION, ASKED THE OTHER WAY ROUND, AND STILL IN THIS FILE.
 *
 * `resolvePolicyColumns` answers "which of a SAVED version's columns may a new arrangement
 * use". The policy builder needs "which of the ids I have chosen in this DRAFT are archived"
 * — a different question about the same fact. Both live here so the archive state is read
 * about a policy column in exactly one module, which is what exit criterion 5 asks for.
 */
export const archivedAmong = <T extends { archivedAt: string | null }>(
  ids: readonly string[],
  summaries: ReadonlyMap<string, T>,
): string[] => ids.filter((id) => Boolean(summaries.get(id)?.archivedAt));

/**
 * The complement. An id the master no longer resolves is KEPT — it is not known to be
 * archived.
 *
 * ⚠️ THE ASYMMETRY WITH `archivedAmong` IS DELIBERATE AND IS THE LINE MOST EASILY BROKEN.
 * A missing entry yields `undefined`, which `Boolean(...)` reads as "not archived" above
 * and `== null` reads as "keep it" here — so an id the map cannot resolve is absent from
 * the archived list AND present in the usable one. That is the intended pair: "the master
 * has never heard of this" is not "this was archived", and dropping an unknown id would
 * quietly delete a column on a stale cache. `policyColumns.test.ts` pins both halves.
 */
export const usableAmong = <T extends { archivedAt: string | null }>(
  ids: readonly string[],
  summaries: ReadonlyMap<string, T>,
): string[] => ids.filter((id) => summaries.get(id)?.archivedAt == null);
